import httpClient from '../../../../api/httpClient';
import { 
  ItemBatch, 
  ItemBatchFilterParams,
  ItemBatchFormData,
  ExpiredBatchesResponse
} from '../types/item-batch.types';

export const itemBatchApi = {
  /**
   * Get paginated list of item batches
   */
  getItemBatches: async (params: ItemBatchFilterParams): Promise<ItemBatch[]> => {
    const response = await httpClient.get<ItemBatch[]>('/item-batches', { params });
    return response.data;
  },

  /**
   * Get all batches for a specific item
   */
  getItemBatchesByItemId: async (itemId: string, includeItem: boolean = false): Promise<ItemBatch[]> => {
    const response = await httpClient.get<ItemBatch[]>(`/item-batches/item/${itemId}`, {
      params: { includeItem }
    });
    return response.data;
  },

  /**
   * Get a single item batch by ID
   */
  getItemBatchById: async (id: string, includeItem: boolean = true): Promise<ItemBatch> => {
    const response = await httpClient.get<ItemBatch>(`/item-batches/${id}`, {
      params: { includeItem }
    });
    return response.data;
  },

  /**
   * Create a new item batch
   */
  createItemBatch: async (data: ItemBatchFormData): Promise<ItemBatch> => {
    try {
      console.log('Sending item batch data:', JSON.stringify(data, null, 2));
      const response = await httpClient.post<ItemBatch>('/item-batches', data, {
        validateStatus: (status) => status < 500
      });
      
      if (response.status >= 400) {
        console.error('Validation error from server:', response.data);
        const errorMessage = (response.data as any)?.message || 'Validation failed';
        throw new Error(errorMessage);
      }
      
      return response.data;
    } catch (error: any) {
      const errorDetails = {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      };
      console.error('Create item batch error details:', errorDetails);
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      throw error;
    }
  },

  /**
   * Update an existing item batch
   */
  updateItemBatch: async (id: string, data: Partial<ItemBatchFormData>): Promise<ItemBatch> => {
    try {
      console.log('Updating item batch:', id, JSON.stringify(data, null, 2));
      const response = await httpClient.patch<ItemBatch>(`/item-batches/${id}`, data, {
        validateStatus: (status) => status < 500
      });
      
      if (response.status >= 400) {
        console.error('Validation error from server:', response.data);
        const errorMessage = (response.data as any)?.message || 'Validation failed';
        throw new Error(errorMessage);
      }
      
      return response.data;
    } catch (error: any) {
      const errorDetails = {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      };
      console.error('Update item batch error details:', errorDetails);
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      throw error;
    }
  },

  /**
   * Delete an item batch (soft delete)
   */
  deleteItemBatch: async (id: string): Promise<ItemBatch> => {
    const response = await httpClient.delete<ItemBatch>(`/item-batches/${id}`);
    return response.data;
  },

  /**
   * Get batches expiring soon
   */
  getExpiringSoon: async (days: number = 30): Promise<ItemBatch[]> => {
    const response = await httpClient.get<ItemBatch[]>('/item-batches/expiring-soon', {
      params: { days }
    });
    return response.data;
  },

  /**
   * Get expired batches
   */
  getExpired: async (): Promise<ItemBatch[]> => {
    const response = await httpClient.get<ItemBatch[]>('/item-batches/expired');
    return response.data;
  },

  /**
   * Mark expired batches as expired status
   */
  markAsExpired: async (): Promise<ExpiredBatchesResponse> => {
    const response = await httpClient.post<ExpiredBatchesResponse>('/item-batches/mark-expired');
    return response.data;
  },
};
